import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  api,
  belegZipWeg,
  type Sammelaktion,
  type SammelErgebnis,
  type ZahlungsSammelErgebnis
} from '../net/apiClient.js'
import { uuidv7 } from '../net/ids.js'
import { rappenAlsText, zuRappen } from '../geld.js'
import { notify, notifyError } from './Toasts.js'
import { Datumsfeld } from './Datumsfeld.js'

/**
 * Mehrere Belege auswählen – einmal gebaut, von Rechnungen und Offerten benutzt.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Warum ein gemeinsames Bauteil
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Rechnungen und Offerten sind zwei Bildschirme, aber eine Handlung: anhaken,
 * sehen wie viele, zusammen herunterladen, Auswahl loswerden. Zweimal gebaut
 * heisst zweimal anders – andere Beschriftung, anderes Verhalten beim
 * Filterwechsel, und an einem der beiden fehlt irgendwann das Aufheben.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Die zwei Regeln, auf die es ankommt
 * ════════════════════════════════════════════════════════════════════════════
 *
 *   Nie von selbst auswählen. Kein Vorhaken, kein «alle Treffer», kein
 *   stilles Übernehmen beim Filterwechsel. Wer etwas gemeinsam herunterlädt,
 *   soll wissen, was darin ist.
 *
 *   «Alle» heisst: alle auf dieser Seite. Die Liste zeigt höchstens die
 *   neuesten fünfhundert Belege; ein Kästchen, das «alle» verspricht und die
 *   Sichtbaren meint, ist eine Zusage, die es nicht hält. Deshalb steht es
 *   genau so da.
 *
 * Verschwindet ein ausgewählter Beleg aus der Liste – weil jemand den Filter
 * wechselt oder sucht –, fällt er aus der Auswahl. Eine unsichtbare Auswahl
 * ist eine Falle: Der Zähler sagt sieben, auf dem Schirm stehen drei.
 */

export interface AuswahlStand {
  /** Die angehakten Kennungen, in der Reihenfolge der Liste. */
  gewaehlt: string[]
  anzahl: number
  istGewaehlt: (id: string) => boolean
  umschalten: (id: string) => void
  alleAufSeite: () => void
  aufheben: () => void
  /** Sind alle sichtbaren Einträge angehakt? */
  alleDa: boolean
}

export function useAuswahl(sichtbar: string[]): AuswahlStand {
  const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set())

  /*
    Die Liste hat sich geändert – was nicht mehr da ist, fliegt raus.

    Verglichen wird über den zusammengesetzten Schlüssel, nicht über das
    Array: React vergleicht Arrays nach Identität, und die ist bei jedem
    Laden neu, auch wenn dieselben Belege darin stehen.
  */
  const schluessel = sichtbar.join(',')
  useEffect(() => {
    setGewaehlt((alt) => {
      if (alt.size === 0) return alt
      const da = new Set(schluessel ? schluessel.split(',') : [])
      const neu = new Set([...alt].filter((id) => da.has(id)))
      return neu.size === alt.size ? alt : neu
    })
  }, [schluessel])

  const umschalten = useCallback((id: string) => {
    setGewaehlt((alt) => {
      const neu = new Set(alt)
      if (neu.has(id)) neu.delete(id)
      else neu.add(id)
      return neu
    })
  }, [])

  const alleDa = sichtbar.length > 0 && sichtbar.every((id) => gewaehlt.has(id))

  const alleAufSeite = useCallback(() => {
    setGewaehlt((alt) => {
      const alleSchon = sichtbar.length > 0 && sichtbar.every((id) => alt.has(id))
      return alleSchon ? new Set() : new Set(sichtbar)
    })
  }, [sichtbar])

  const aufheben = useCallback(() => setGewaehlt(new Set()), [])

  /* In der Reihenfolge der Liste, nicht in der des Anklickens – das ZIP soll sortiert sein. */
  const geordnet = useMemo(() => sichtbar.filter((id) => gewaehlt.has(id)), [sichtbar, gewaehlt])

  return {
    gewaehlt: geordnet,
    anzahl: geordnet.length,
    istGewaehlt: (id) => gewaehlt.has(id),
    umschalten,
    alleAufSeite,
    aufheben,
    alleDa
  }
}

/** Das Kästchen an einer Zeile. Es schluckt den Klick – die Zeile soll sich nicht öffnen. */
export function Auswahlkasten({
  id,
  stand,
  titel
}: {
  id: string
  stand: AuswahlStand
  titel: string
}): JSX.Element {
  return (
    <label className="o-wahlkasten" onClick={(e) => e.stopPropagation()}>
      <input
        type="checkbox"
        id={`wahl-${id}`}
        checked={stand.istGewaehlt(id)}
        onChange={() => stand.umschalten(id)}
        aria-label={titel}
      />
    </label>
  )
}

/**
 * Das Kästchen für «alle auf dieser Seite».
 *
 * `nurKasten` lässt die Beschriftung weg – im Kopf einer Tabelle steht sie in
 * der Spalte darunter und wäre doppelt. Der Vorlesehinweis bleibt in beiden
 * Fällen vollständig.
 */
export function AlleAufSeite({
  stand,
  anzahl,
  nurKasten = false
}: {
  stand: AuswahlStand
  anzahl: number
  nurKasten?: boolean
}): JSX.Element {
  return (
    <label className="o-wahlkasten o-wahlkasten-kopf">
      <input
        type="checkbox"
        id="wahl-alle"
        checked={stand.alleDa}
        onChange={stand.alleAufSeite}
        aria-label={`Alle ${anzahl} auf dieser Seite auswählen`}
      />
      {!nurKasten && <span className="o-soft small">Alle auf dieser Seite</span>}
    </label>
  )
}

/**
 * Ein ausgewählter Beleg, so weit die Leiste ihn kennen muss.
 *
 * Mehr braucht sie nicht: Nummer für die Rückmeldung, Zustand für die Frage,
 * welche Aktion überhaupt passt, und ob ein PDF vorliegt.
 */
export interface AuswahlBeleg {
  id: string
  nummer: string | null
  status: string
  hasPdf: boolean
  /**
   * Wie der Beleg in einer Frage heissen soll.
   *
   * Nicht einfach die Nummer: Ein Entwurf hat keine, und «Entwurf ohne
   * Nummer», dreimal untereinander, sagt niemandem, was er gerade verwirft.
   * Deshalb kommt der Name von dort, wo auch der Kundenname steht – aus der
   * Liste.
   */
  bezeichnung: string
  /**
   * Was an dieser Rechnung noch offen ist, in Rappen.
   *
   * Nur Rechnungen haben das – eine Offerte wird nicht bezahlt. Fehlt der
   * Wert, gibt es für diesen Beleg keine Zahlungserfassung.
   */
  offenRp?: number
}

/** Höchstens so viele Entwürfe lassen sich in einem Durchgang abschliessen. */
export const ABSCHLUSS_HOECHSTZAHL = 10

/**
 * Die Leiste, die erscheint, sobald etwas angehakt ist.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Die Aktionen richten sich nach der Auswahl, nicht umgekehrt
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Ein Entwurf lässt sich abschliessen und verwerfen. Ein ausgestellter Beleg
 * lässt sich stornieren und herunterladen. Ein stornierter lässt sich nur noch
 * lesen. Die Leiste zeigt deshalb nicht alle Knöpfe und schaltet die unpassenden
 * aus, sondern zeigt die, die passen – jeden mit der Zahl, für die er gilt.
 *
 * Deshalb steht unter dem Zähler, woraus die Auswahl besteht. Ohne diese Zeile
 * sieht man «5 ausgewählt» und daneben «2 stornieren» und fragt sich, wo die
 * anderen drei geblieben sind.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Eine Frage, nicht fünf
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Was sich nicht zurücknehmen lässt, wird einmal gefragt – mit der Zahl und
 * den Folgen. Nicht je Beleg: Wer fünfmal dieselbe Frage wegklickt, liest beim
 * dritten Mal nicht mehr, und das ist das Gegenteil von Sicherheit.
 */
export function Auswahlleiste({
  stand,
  belege,
  was,
  aufGetan
}: {
  stand: AuswahlStand
  /** Die ausgewählten Belege, in der Reihenfolge der Liste. */
  belege: AuswahlBeleg[]
  was: 'Rechnung' | 'Offerte'
  /** Die Liste neu laden – nach allem, was etwas verändert hat. */
  aufGetan: () => void | Promise<void>
}): JSX.Element | null {
  const [frage, setFrage] = useState<Sammelaktion | null>(null)
  const [zahlungsfrage, setZahlungsfrage] = useState(false)
  const [hoehe, setHoehe] = useState(0)
  const wache = useRef<ResizeObserver | null>(null)

  /*
    Wie hoch die Leiste gerade ist – und zwar gemessen, nicht geschätzt.

    Am Telefon hängt sie am Fensterrand und ist damit aus dem Fluss; der
    Platzhalter darunter hält den Platz frei, damit sie den letzten Beleg
    nicht verdeckt. Dort stand ein fester Wert, und der stimmte genau so
    lange, wie die Leiste eine Knopfzeile hatte. Mit den Sammelaktionen wächst
    sie je nach Auswahl – also wird gemessen.

    Gemessen wird an der Verweisfunktion und nicht in einem `useEffect` mit
    Abhängigkeiten: Die Leiste verschwindet, indem dieses Bauteil `null`
    liefert, und dabei wird kein Effekt aufgeräumt. Eine Wache hinge danach an
    einem Kasten, den es nicht mehr gibt. So hingegen fällt der Verweis beim
    Verschwinden auf `null`, und genau dort wird sie gelöst.
  */
  const leiste = useCallback((el: HTMLDivElement | null) => {
    wache.current?.disconnect()
    wache.current = null
    if (!el) {
      setHoehe(0)
      return
    }
    setHoehe(el.getBoundingClientRect().height)
    if (typeof ResizeObserver === 'undefined') return
    const w = new ResizeObserver(() => setHoehe(el.getBoundingClientRect().height))
    w.observe(el)
    wache.current = w
  }, [])

  if (stand.anzahl === 0) return null

  const mehrzahl = was === 'Rechnung' ? 'Rechnungen' : 'Offerten'
  const mitPdf = belege.filter((b) => b.hasPdf).map((b) => b.id)
  const entwuerfe = belege.filter((b) => b.status === 'entwurf')
  const stornierbar = belege.filter((b) => b.status !== 'entwurf' && b.status !== 'storniert')
  const storniert = belege.filter((b) => b.status === 'storniert')
  /*
    Worauf sich eine Zahlung erfassen lässt: eine ausgestellte Rechnung, an
    der noch Geld fehlt. Ein Entwurf nicht (er stand nie bei einem Kunden),
    eine stornierte nicht, eine bezahlte nicht – und eine Offerte schon
    deshalb nicht, weil sie keinen offenen Betrag mitbringt.
  */
  const zahlbar = belege.filter(
    (b) => b.status !== 'entwurf' && b.status !== 'storniert' && (b.offenRp ?? 0) > 0
  )
  const ohnePdf = stand.anzahl - mitPdf.length
  const gemischt = entwuerfe.length > 0 && stornierbar.length > 0

  const zusammensetzung = [
    entwuerfe.length > 0
      ? `${entwuerfe.length} ${entwuerfe.length === 1 ? 'Entwurf' : 'Entwürfe'}`
      : '',
    stornierbar.length > 0 ? `${stornierbar.length} ausgestellt` : '',
    storniert.length > 0 ? `${storniert.length} storniert` : '',
    /*
      Der offene Betrag steht dort, wo er zur Entscheidung gehört: neben dem
      Knopf, der ihn verbucht. Ohne die Summe müsste man ihn aus der Liste
      zusammenzählen, bevor man den Kontoauszug danebenlegt.
    */
    zahlbar.length > 0
      ? `CHF ${rappenAlsText(zahlbar.reduce((n, b) => n + (b.offenRp ?? 0), 0))} offen`
      : '',
    /*
      «Ohne PDF» nur dann, wenn es etwas erklärt: wenn gar kein Archiv
      möglich ist. Liegt zu zweien von dreien ein PDF vor, sagt der Knopf
      «2 PDF als ZIP» das schon – und auf einem Telefon ist jede Zeile, die
      nichts Neues sagt, eine Zeile weniger Liste.
    */
    mitPdf.length === 0 && ohnePdf > 0 ? `${ohnePdf} ohne PDF` : ''
  ].filter(Boolean)

  async function fertig(): Promise<void> {
    setFrage(null)
    setZahlungsfrage(false)
    stand.aufheben()
    await aufGetan()
  }

  return (
    <>
      <div
        className="o-auswahlleiste"
        id="auswahlleiste"
        role="region"
        aria-label="Auswahl"
        ref={leiste}
      >
        {/*
          Links steht, WAS ausgewählt ist – und gleich daneben der Weg, es
          wieder loszuwerden. «Auswahl aufheben» gehört zur Auswahl und nicht
          zu den Belegaktionen; dort stand es, und am Telefon kostete es eine
          ganze Knopfzeile, die der Liste fehlte.
        */}
        <div className="o-auswahl-kopf">
          <span className="o-auswahl-zahl">
            <b id="auswahl-anzahl">{stand.anzahl}</b>{' '}
            {stand.anzahl === 1 ? `${was} ausgewählt` : `${mehrzahl} ausgewählt`}
            {zusammensetzung.length > 0 && (
              <i className="o-soft" id="auswahl-zusammensetzung">
                {' '}
                · {zusammensetzung.join(' · ')}
              </i>
            )}
            {gemischt && (
              <i className="o-soft o-auswahl-hinweis" id="auswahl-gemischt">
                Jede Aktion gilt für die passenden Belege.
              </i>
            )}
          </span>
          <button
            type="button"
            className="o-btn o-btn-ghost o-auswahl-weg"
            id="auswahl-aufheben"
            onClick={stand.aufheben}
          >
            Auswahl aufheben
          </button>
        </div>
        <div className="o-auswahl-aktionen">
          {mitPdf.length > 0 && (
            <a className="o-btn o-btn-primary" id="auswahl-zip" href={belegZipWeg(mitPdf)} download>
              {mitPdf.length === 1 ? 'PDF herunterladen' : `${mitPdf.length} PDF als ZIP`}
            </a>
          )}
          {zahlbar.length > 0 && (
            <button
              type="button"
              className="o-btn"
              id="auswahl-zahlungen"
              onClick={() => setZahlungsfrage(true)}
            >
              {zahlbar.length === 1 ? 'Zahlung erfassen' : `${zahlbar.length} Zahlungen erfassen`}
            </button>
          )}
          {entwuerfe.length > 0 && (
            <button
              type="button"
              className="o-btn"
              id="auswahl-abschliessen"
              onClick={() => setFrage('abschliessen')}
            >
              {entwuerfe.length} abschliessen
            </button>
          )}
          {entwuerfe.length > 0 && (
            <button
              type="button"
              className="o-btn"
              id="auswahl-verwerfen"
              onClick={() => setFrage('verwerfen')}
            >
              {entwuerfe.length} verwerfen
            </button>
          )}
          {stornierbar.length > 0 && (
            <button
              type="button"
              className="o-btn"
              id="auswahl-stornieren"
              onClick={() => setFrage('stornieren')}
            >
              {stornierbar.length} stornieren
            </button>
          )}
          {mitPdf.length === 0 &&
            entwuerfe.length === 0 &&
            stornierbar.length === 0 &&
            zahlbar.length === 0 && (
              <span className="o-soft small" id="auswahl-nichts-moeglich">
                Zu stornierten Belegen ohne PDF gibt es nichts mehr zu tun.
              </span>
            )}
        </div>
      </div>
      {/*
        Der Platzhalter am Fuss der Seite.

        Am Telefon hängt die Leiste am Fensterrand (`position: fixed`) und ist
        damit aus dem Fluss – sie legt sich über das Letzte, was in der Liste
        steht. Vorher hielt ein `padding-bottom` an `.o-belege` den Platz frei;
        das half den Rechnungen und nicht den Offerten, weil dort eine Tabelle
        steht und keine Belegliste. Dieser Kasten steht hinter beiden und hält
        den Platz dort frei, wo er fehlt: am Ende des Inhalts.

        Seine Höhe ist die gemessene Höhe der Leiste plus ihr Abstand zum Rand.
        Kennt der Browser ResizeObserver nicht, bleibt der Wert aus dem
        Stilblatt stehen – dann ist der Streifen vielleicht etwas zu hoch,
        aber nie zu niedrig.
      */}
      <div
        className="o-auswahl-platz"
        aria-hidden="true"
        style={hoehe > 0 ? { height: hoehe + 12 } : undefined}
      />
      {zahlungsfrage && (
        <ZahlungsFrage belege={zahlbar} aufZu={() => setZahlungsfrage(false)} aufFertig={fertig} />
      )}
      {frage && (
        <SammelFrage
          aktion={frage}
          was={was}
          belege={frage === 'stornieren' ? stornierbar : entwuerfe}
          aufZu={() => setFrage(null)}
          aufFertig={fertig}
        />
      )}
    </>
  )
}

/**
 * Der Datumsbereich über einer Belegliste.
 *
 * Drei Felder und ein Rücksetzer, mehr nicht. Die Datumsarten kommen von
 * aussen, weil eine Rechnung fällig wird und eine Offerte abläuft – dieselbe
 * Leiste, andere Beschriftung.
 *
 * Die beiden Tage stehen in `ui/Datumsfeld.tsx`, nicht in einem eigenen
 * `<input type="date">`. Der Unterschied ist nicht kosmetisch: dort öffnet
 * ein Klick irgendwo im Feld den Kalender, und rechts steht ein Zeichen, das
 * zeigt, dass überhaupt etwas aufgeht. Ein nacktes Datumsfeld sieht in Safari
 * aus wie ein Textfeld – wer das nicht weiss, tippt. Zwei Datumsfelder im
 * Büro, die sich verschieden bedienen lassen, sind ein Fehler für sich.
 *
 * `von` begrenzt `bis` und umgekehrt: ein verdrehter Zeitraum ist keine
 * Auswahl, sondern eine leere Liste mit unklarem Grund. Dieselbe Kopplung
 * trägt der Leistungszeitraum in den Offerten schon lange.
 */
export function Datumsleiste({
  arten,
  wert,
  aufWert,
  kennung
}: {
  arten: Array<{ id: string; label: string }>
  wert: { art: string; von: string; bis: string }
  aufWert: (w: { art: string; von: string; bis: string }) => void
  kennung: string
}): JSX.Element {
  const aktiv = wert.von !== '' || wert.bis !== ''
  return (
    <div className="o-datumsleiste">
      <div className="o-field">
        <label htmlFor={`${kennung}-datumsart`}>Zeitraum nach</label>
        <select
          id={`${kennung}-datumsart`}
          value={wert.art}
          onChange={(e) => aufWert({ ...wert, art: e.target.value })}
        >
          {arten.map((a) => (
            <option key={a.id} value={a.id}>
              {a.label}
            </option>
          ))}
        </select>
      </div>
      <div className="o-field">
        <label htmlFor={`${kennung}-von`}>von</label>
        <Datumsfeld
          id={`${kennung}-von`}
          value={wert.von}
          max={wert.bis || undefined}
          onChange={(v) => aufWert({ ...wert, von: v })}
        />
      </div>
      <div className="o-field">
        <label htmlFor={`${kennung}-bis`}>bis</label>
        <Datumsfeld
          id={`${kennung}-bis`}
          value={wert.bis}
          min={wert.von || undefined}
          onChange={(v) => aufWert({ ...wert, bis: v })}
        />
      </div>
      {aktiv && (
        <button
          type="button"
          className="o-btn o-btn-ghost o-feld-aktion"
          id={`${kennung}-datum-zurueck`}
          onClick={() => aufWert({ ...wert, von: '', bis: '' })}
        >
          Zeitraum aufheben
        </button>
      )}
    </div>
  )
}

/**
 * Die eine Frage vor einer Sammelaktion – und danach die Antwort.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Warum ein Fenster und kein «Sicher?» je Beleg
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Weil fünf gleiche Fragen hintereinander keine Sicherheit sind, sondern eine
 * Übung im Wegklicken. Hier steht einmal, wie viele Belege es trifft, was
 * danach anders ist und welche es genau sind.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Dasselbe Fenster zeigt danach, was passiert ist
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Ein Durchgang über mehrere Belege endet selten mit «alles gut» oder «alles
 * schlecht»: Der eine war schon storniert, dem anderen fehlt eine Position.
 * Eine kurze Meldung, die über den Bildschirm wandert, kann das nicht tragen –
 * also bleibt das Fenster offen und zeigt beide Listen, Beleg für Beleg.
 */
function SammelFrage({
  aktion,
  belege,
  was,
  aufZu,
  aufFertig
}: {
  aktion: Sammelaktion
  belege: AuswahlBeleg[]
  was: 'Rechnung' | 'Offerte'
  aufZu: () => void
  aufFertig: () => void | Promise<void>
}): JSX.Element {
  const [grund, setGrund] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const [ergebnis, setErgebnis] = useState<SammelErgebnis | null>(null)

  /* Der Server kennt Kennungen und Nummern, nicht die Namen aus der Liste. */
  const nenne = (id: string): string => belege.find((b) => b.id === id)?.bezeichnung ?? 'Beleg'

  const zuViele = aktion === 'abschliessen' && belege.length > ABSCHLUSS_HOECHSTZAHL
  /*
    Eine Rechnung wird nur mit Grund storniert – er steht später in der Akte.
    Bei einer Offerte ist er freiwillig: «der Kunde hat sich nicht mehr
    gemeldet» tippt niemand gern, und niemand muss es.
  */
  const grundNoetig = aktion === 'stornieren' && was === 'Rechnung'
  const bereit = !zuViele && (!grundNoetig || grund.trim().length > 0)

  const TITEL: Record<Sammelaktion, string> = {
    abschliessen:
      belege.length === 1 ? 'Entwurf abschliessen?' : `${belege.length} Entwürfe abschliessen?`,
    verwerfen: belege.length === 1 ? 'Entwurf verwerfen?' : `${belege.length} Entwürfe verwerfen?`,
    stornieren:
      belege.length === 1
        ? `${was} stornieren?`
        : `${belege.length} ${was === 'Rechnung' ? 'Rechnungen' : 'Offerten'} stornieren?`
  }

  const FOLGEN: Record<Sammelaktion, string> = {
    abschliessen:
      'Jeder Entwurf bekommt seine Nummer und sein PDF. Eine vergebene Nummer ' +
      'lässt sich nicht zurücknehmen – ein abgeschlossener Beleg kann danach nur ' +
      'noch storniert werden.',
    verwerfen:
      'Die Entwürfe werden gelöscht, mit allen Positionen. Reservierte ' +
      'Rapportzeilen werden wieder frei und lassen sich neu verrechnen. ' +
      'Rückgängig machen lässt sich das nicht.',
    stornieren:
      'Die Belege bleiben lesbar und behalten ihre Nummer – storniert wird ' +
      'nicht gelöscht. Sie zählen nicht mehr zum Umsatz, und reservierte ' +
      'Rapportzeilen werden wieder frei.'
  }

  /* Abgeschlossen, aber ohne Datei – siehe die Knöpfe in der Ergebnisansicht. */
  const ohnePdf = (ergebnis?.erledigt ?? []).filter((e) => e.pdfFehlt)

  /**
   * Die kurze Bestätigung, wenn alles geklappt hat.
   *
   * Ein Satz, der nach ein paar Sekunden wieder weg ist – nicht eine Liste,
   * die weggeklickt werden muss. Bei genau einem Beleg steht seine Nummer
   * darin; bei mehreren die Zahl, denn fünf Nummern in einer Zeile liest
   * niemand.
   */
  function bestaetigung(fertig: SammelErgebnis['erledigt']): string {
    const n = fertig.length
    const eins = n === 1 ? (fertig[0].nummer ?? nenne(fertig[0].id)) : null
    if (aktion === 'verwerfen') {
      return eins ? `Entwurf ${eins} verworfen.` : `${n} Entwürfe verworfen.`
    }
    if (aktion === 'abschliessen') {
      return eins
        ? `Abgeschlossen als ${eins} – mit PDF.`
        : `${n} Entwürfe abgeschlossen – mit Nummer und PDF.`
    }
    return eins
      ? `${was} ${eins} storniert.`
      : `${n} ${was === 'Rechnung' ? 'Rechnungen' : 'Offerten'} storniert.`
  }

  /**
   * Ein Durchgang über die Belege – der einzige Weg, der den Server ruft.
   *
   * ──────────────────────────────────────────────────────────────────────────
   * Zwei Dinge sind hier wichtig, und beide waren falsch
   * ──────────────────────────────────────────────────────────────────────────
   *
   * 1. `setLaeuft(false)` steht im `finally` und nicht nur im `catch`.
   *
   *    Vorher gab es daneben ein zweites `los()`, das die Marke im Erfolgsfall
   *    stehen liess. Das Ergebnisfenster hängt mit `disabled={laeuft}` an ihr –
   *    «Schliessen», «PDF nachholen» und «Erneut versuchen» waren danach alle
   *    drei totgeschaltet, und die Dialogschicht nahm die Oberfläche mit.
   *    Gemessen auf Production nachgestellt: Server nach 441 ms fertig, «2
   *    erledigt» stand da, `#sammelergebnis-schliessen` war `disabled` und
   *    liess sich nicht anklicken. Keine Langsamkeit, eine klemmende Marke.
   *
   * 2. Ist alles geglückt, gibt es nichts zu entscheiden.
   *
   *    Dann schliesst der Dialog, die Auswahl fällt weg, die Liste lädt neu,
   *    und ein kurzer Satz sagt, was geschehen ist. Das Fenster bleibt nur
   *    offen, wenn es etwas zu zeigen gibt: ein Beleg, der nicht ging, oder
   *    eine Nummer ohne PDF – beides braucht eine Entscheidung und darf nicht
   *    mit einer Meldung über den Bildschirm wandern.
   */
  async function laufen(ids: string[]): Promise<void> {
    setLaeuft(true)
    try {
      const antwort = await api.belegSammelaktion(aktion, ids, grund, uuidv7())
      const offeneFrage = antwort.fehler.length > 0 || antwort.erledigt.some((e) => e.pdfFehlt)
      if (!offeneFrage) {
        /* Die früheren Erfolge mitzählen: «Erneut versuchen» ist der zweite Teil
           desselben Durchgangs, und der Satz soll den ganzen nennen. */
        notify.ok(bestaetigung([...(ergebnis?.erledigt ?? []), ...antwort.erledigt]))
        await aufFertig()
        return
      }
      /* Teilweise: zeigen, was war UND was ist – sonst verschwinden beim
         zweiten Versuch die Belege aus der Liste, die beim ersten geglückt sind. */
      setErgebnis((alt) =>
        alt ? { ...antwort, erledigt: [...alt.erledigt, ...antwort.erledigt] } : antwort
      )
    } catch (err) {
      /*
        Hier landet nur, was den ganzen Durchgang betrifft – fehlende Rechte,
        zu viele Belege, kein Netz. Was einzelne Belege betrifft, steht in der
        Antwort und nicht in einem Fehler.
      */
      notifyError(err)
    } finally {
      setLaeuft(false)
    }
  }

  /** Nur die Belege noch einmal, bei denen es nicht geklappt hat. */
  async function nochmal(): Promise<void> {
    await laufen((ergebnis?.fehler ?? []).map((f) => f.id))
  }

  /**
   * Die fehlenden PDF nachziehen – über den vorhandenen Einzelweg.
   *
   * Je Beleg ein Aufruf und keine neue Sammelroute: Das Drucken ist schon
   * einzeln erreichbar, und ein zweiter Weg dorthin wäre eine zweite
   * Umsetzung desselben.
   */
  async function pdfNachholen(): Promise<void> {
    setLaeuft(true)
    const geschafft: string[] = []
    const offen: Array<{ id: string; nummer: string | null; code: string; text: string }> = []
    for (const e of ohnePdf) {
      try {
        const antwort = await api.regeneratePdf(e.id, uuidv7())
        if (antwort.pdfReady) geschafft.push(e.id)
        else
          offen.push({
            id: e.id,
            nummer: e.nummer,
            code: 'PDF',
            text: antwort.error ?? 'Das PDF liess sich nicht drucken.'
          })
      } catch (err) {
        offen.push({
          id: e.id,
          nummer: e.nummer,
          code: 'PDF',
          text: err instanceof Error ? err.message : 'Das PDF liess sich nicht drucken.'
        })
      }
    }
    setErgebnis((alt) =>
      alt
        ? {
            ...alt,
            erledigt: alt.erledigt.map((e) =>
              geschafft.includes(e.id)
                ? { ...e, pdfFehlt: false, text: `Abgeschlossen als ${e.nummer} – PDF nachgeholt.` }
                : e
            ),
            fehler: [...alt.fehler, ...offen]
          }
        : alt
    )
    setLaeuft(false)
  }

  /** Der erste Durchgang: alle ausgewählten Belege. */
  async function los(): Promise<void> {
    await laufen(belege.map((b) => b.id))
  }

  return (
    <div className="o-dialog-grund" id="sammelfrage">
      <div className="o-dialog">
        {!ergebnis ? (
          <>
            <h3 id="sammelfrage-titel">{TITEL[aktion]}</h3>
            <p>{FOLGEN[aktion]}</p>

            <ul className="o-sammel-liste" id="sammelfrage-belege">
              {belege.slice(0, 8).map((b) => (
                <li key={b.id}>{b.bezeichnung}</li>
              ))}
              {belege.length > 8 && <li className="o-soft">und {belege.length - 8} weitere</li>}
            </ul>

            {zuViele && (
              <p className="o-warnzeile" id="sammelfrage-zuviele" role="alert">
                Auf einmal gehen höchstens {ABSCHLUSS_HOECHSTZAHL} Entwürfe – für jeden wird ein PDF
                gedruckt, und das dauert. Hake weniger an und wiederhole den Rest danach.
              </p>
            )}

            {aktion === 'stornieren' && (
              <div className="o-field">
                <label htmlFor="sammel-grund">Grund {grundNoetig ? '' : '(freiwillig)'}</label>
                <input
                  id="sammel-grund"
                  type="text"
                  maxLength={500}
                  value={grund}
                  autoComplete="off"
                  placeholder={
                    was === 'Rechnung'
                      ? 'Steht später in der Akte, z. B. «doppelt verrechnet»'
                      : 'z. B. «Kunde hat sich nicht mehr gemeldet»'
                  }
                  onChange={(e) => setGrund(e.target.value)}
                />
                {grundNoetig && grund.trim().length === 0 && (
                  <span className="o-soft small">
                    Eine stornierte Rechnung braucht einen Grund – er gehört in die Akte.
                  </span>
                )}
              </div>
            )}

            <div className="office-actions">
              <button
                type="button"
                className="o-btn o-btn-primary"
                id="sammelfrage-los"
                disabled={!bereit || laeuft}
                onClick={() => void los()}
              >
                {laeuft ? 'Läuft …' : TITEL[aktion].replace('?', '')}
              </button>
              <button
                type="button"
                className="o-btn o-btn-ghost"
                id="sammelfrage-abbruch"
                disabled={laeuft}
                onClick={aufZu}
              >
                Abbrechen
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 id="sammelergebnis-titel">
              {ergebnis.fehler.length === 0
                ? `${ergebnis.erledigt.length} erledigt`
                : `${ergebnis.erledigt.length} erledigt, ${ergebnis.fehler.length} nicht`}
            </h3>

            {ergebnis.erledigt.length > 0 && (
              <ul className="o-sammel-liste" id="sammelergebnis-erledigt">
                {ergebnis.erledigt.map((e) => (
                  <li key={e.id}>
                    <b>{e.nummer ?? nenne(e.id)}</b> – {e.text}
                  </li>
                ))}
              </ul>
            )}

            {ergebnis.fehler.length > 0 && (
              <ul className="o-sammel-liste o-sammel-fehler" id="sammelergebnis-fehler">
                {ergebnis.fehler.map((f) => (
                  <li key={f.id}>
                    <b>{f.nummer ?? nenne(f.id)}</b> – {f.text}
                  </li>
                ))}
              </ul>
            )}

            {/*
              Was nicht geklappt hat, lässt sich hier gezielt nachziehen –
              und nur das. Ohne diese beiden Knöpfe müsste jemand die
              betroffenen Belege aus der Liste wieder zusammensuchen, und
              genau das war die Arbeit, die der Sammelabschluss abnehmen soll.

              Zwei verschiedene Dinge, zwei verschiedene Knöpfe:

                «PDF nachholen» – der Beleg IST abgeschlossen und hat seine
                Nummer, es fehlt die Datei. Dafür gibt es den vorhandenen Weg
                «PDF erneut erzeugen», hier für mehrere auf einmal.

                «Erneut versuchen» – der Beleg ist unverändert. Derselbe
                Aufruf noch einmal, nur für die, die es betrifft.
            */}
            <div className="office-actions">
              {ohnePdf.length > 0 && (
                <button
                  type="button"
                  className="o-btn"
                  id="sammelergebnis-pdf"
                  disabled={laeuft}
                  onClick={() => void pdfNachholen()}
                >
                  {laeuft ? 'Läuft …' : `PDF nachholen (${ohnePdf.length})`}
                </button>
              )}
              {ergebnis.fehler.length > 0 && (
                <button
                  type="button"
                  className="o-btn"
                  id="sammelergebnis-nochmal"
                  disabled={laeuft}
                  onClick={() => void nochmal()}
                >
                  {laeuft ? 'Läuft …' : `${ergebnis.fehler.length} erneut versuchen`}
                </button>
              )}
              <button
                type="button"
                className="o-btn o-btn-primary"
                id="sammelergebnis-schliessen"
                disabled={laeuft}
                onClick={() => void aufFertig()}
              >
                Schliessen
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

/**
 * Zahlungseingänge für mehrere Rechnungen erfassen.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Wofür das gebaut ist
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Jemand sitzt mit dem Kontoauszug vor der Liste. Sieben Zahlungen sind
 * eingegangen, alle am selben Tag. Ohne diesen Kasten heisst das: siebenmal
 * eine Rechnung öffnen, siebenmal dasselbe Datum tippen, siebenmal speichern.
 *
 * Deshalb ein Datum für alle und ein Betrag je Zeile, vorausgefüllt mit dem,
 * was offen ist. Im Regelfall wird nichts getippt: Datum stimmt, Beträge
 * stimmen, verbuchen.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Warum die Beträge trotzdem offen stehen
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Weil Teilzahlungen vorkommen und die Zahlungslogik sie kennt – ein
 * Kontoauszug zeigt nun einmal auch Beträge, die nicht aufgehen. Wer eine
 * Zeile ändert, sieht sofort, was danach offen bleibt. Was nicht passieren
 * soll, ist eine stille Rundung: Eine Eingabe, die keine Zahl ergibt, wird
 * null, und null lässt sich nicht verbuchen.
 */
function ZahlungsFrage({
  belege,
  aufZu,
  aufFertig
}: {
  belege: AuswahlBeleg[]
  aufZu: () => void
  aufFertig: () => void | Promise<void>
}): JSX.Element {
  const heute = new Date().toISOString().slice(0, 10)
  const [datum, setDatum] = useState(heute)
  const [betraege, setBetraege] = useState<Record<string, string>>(() =>
    Object.fromEntries(belege.map((b) => [b.id, ((b.offenRp ?? 0) / 100).toFixed(2)]))
  )
  const [laeuft, setLaeuft] = useState(false)
  const [ergebnis, setErgebnis] = useState<ZahlungsSammelErgebnis | null>(null)

  const posten = belege
    .map((b) => ({ beleg: b, rappen: zuRappen(betraege[b.id] ?? '') }))
    .filter((p) => p.rappen > 0)
  const summe = posten.reduce((n, p) => n + p.rappen, 0)
  const ueberzahlt = posten.filter((p) => p.rappen > (p.beleg.offenRp ?? 0))
  const leer = belege.length - posten.length
  const bereit = posten.length > 0 && datum !== '' && datum <= heute

  async function los(): Promise<void> {
    setLaeuft(true)
    try {
      setErgebnis(
        await api.zahlungenSammeln(
          { paidAt: datum, posten: posten.map((p) => ({ id: p.beleg.id, amountRp: p.rappen })) },
          uuidv7()
        )
      )
    } catch (err) {
      /* Was den ganzen Vorgang betrifft – Rechte, Datum, kein Netz. Was
         einzelne Rechnungen betrifft, steht in der Antwort. */
      notifyError(err)
    } finally {
      /*
        Im `finally`, nicht nur im `catch` – dieselbe klemmende Marke wie in
        `SammelFrage`. Hier blieb sie ohne Folgen, weil «Schliessen» in der
        Ergebnisansicht nicht an ihr hängt; stehen bleiben darf sie trotzdem
        nicht, sonst klemmt der nächste Knopf, der daran gehängt wird.
      */
      setLaeuft(false)
    }
  }

  return (
    <div className="o-dialog-grund" id="zahlungsfrage">
      <div className="o-dialog o-dialog-breit">
        {!ergebnis ? (
          <>
            <h3 id="zahlungsfrage-titel">
              {belege.length === 1 ? 'Zahlung erfassen' : `${belege.length} Zahlungen erfassen`}
            </h3>
            <p>
              Hier wird festgehalten, dass Geld eingegangen ist – es wird nichts überwiesen. Der
              Zustand jeder Rechnung ergibt sich danach aus ihren Zahlungen.
            </p>

            <div className="o-field o-zahlung-datum">
              <label htmlFor="sammel-zahldatum">Zahlungsdatum für alle</label>
              <Datumsfeld id="sammel-zahldatum" value={datum} max={heute} onChange={setDatum} />
            </div>

            <div className="o-tabelle">
              <table className="o-data" id="zahlungsfrage-posten">
                <thead>
                  <tr>
                    <th>Rechnung</th>
                    <th className="o-num" style={{ width: 120 }}>
                      Offen
                    </th>
                    <th className="o-num" style={{ width: 140 }}>
                      Betrag CHF
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {belege.map((b) => {
                    const rappen = zuRappen(betraege[b.id] ?? '')
                    const rest = (b.offenRp ?? 0) - rappen
                    return (
                      <tr key={b.id}>
                        <td data-feld="Rechnung">
                          {b.bezeichnung}
                          {rappen > 0 && rest > 0 && (
                            <i className="o-soft" id={`zrest-${b.id}`}>
                              {' '}
                              · danach offen CHF {rappenAlsText(rest)}
                            </i>
                          )}
                          {rappen > (b.offenRp ?? 0) && (
                            <i className="o-soft warn" id={`zmehr-${b.id}`}>
                              {' '}
                              · mehr als offen
                            </i>
                          )}
                        </td>
                        <td className="o-num" data-feld="Offen">
                          {rappenAlsText(b.offenRp ?? 0)}
                        </td>
                        <td className="o-num">
                          <input
                            autoComplete="off"
                            inputMode="decimal"
                            className="o-zahlung-betrag"
                            id={`zbetrag-${b.id}`}
                            aria-label={`Betrag für ${b.bezeichnung}`}
                            value={betraege[b.id] ?? ''}
                            onChange={(e) =>
                              setBetraege((alt) => ({ ...alt, [b.id]: e.target.value }))
                            }
                          />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <p className="o-zahlung-summe" id="zahlungsfrage-summe">
              <b>
                {posten.length} von {belege.length} · CHF {rappenAlsText(summe)}
              </b>
              {leer > 0 && (
                <i className="o-soft">
                  {' '}
                  · {leer} ohne Betrag {leer === 1 ? 'bleibt' : 'bleiben'} unverbucht
                </i>
              )}
            </p>

            {ueberzahlt.length > 0 && (
              <p className="o-warnzeile" id="zahlungsfrage-ueberzahlt" role="alert">
                {ueberzahlt.length === 1
                  ? 'Bei einer Rechnung liegt der Betrag über dem offenen Rest. '
                  : `Bei ${ueberzahlt.length} Rechnungen liegt der Betrag über dem offenen Rest. `}
                Das geht – sie gilt danach als überbezahlt und steht so in der Liste.
              </p>
            )}

            <div className="office-actions">
              <button
                type="button"
                className="o-btn o-btn-primary"
                id="zahlungsfrage-los"
                disabled={!bereit || laeuft}
                onClick={() => void los()}
              >
                {laeuft ? 'Verbucht …' : `CHF ${rappenAlsText(summe)} verbuchen`}
              </button>
              <button
                type="button"
                className="o-btn o-btn-ghost"
                id="zahlungsfrage-abbruch"
                disabled={laeuft}
                onClick={aufZu}
              >
                Abbrechen
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 id="zahlungsergebnis-titel">
              {ergebnis.fehler.length === 0
                ? `${ergebnis.erfasst.length} verbucht`
                : `${ergebnis.erfasst.length} verbucht, ${ergebnis.fehler.length} nicht`}
            </h3>

            {ergebnis.erfasst.length > 0 && (
              <ul className="o-sammel-liste" id="zahlungsergebnis-erfasst">
                {ergebnis.erfasst.map((e) => (
                  <li key={e.id}>
                    <b>{e.nummer ?? 'Rechnung'}</b> – CHF {rappenAlsText(e.amountRp)} · {e.text}
                  </li>
                ))}
              </ul>
            )}

            {ergebnis.fehler.length > 0 && (
              <ul className="o-sammel-liste o-sammel-fehler" id="zahlungsergebnis-fehler">
                {ergebnis.fehler.map((f) => (
                  <li key={f.id}>
                    <b>{belege.find((b) => b.id === f.id)?.bezeichnung ?? 'Rechnung'}</b> – {f.text}
                  </li>
                ))}
              </ul>
            )}

            <div className="office-actions">
              <button
                type="button"
                className="o-btn o-btn-primary"
                id="zahlungsergebnis-schliessen"
                onClick={() => void aufFertig()}
              >
                Schliessen
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
